import React, { useState, useCallback } from 'react';
import { WizardShell } from './WizardShell';
import { useWizardNavigation } from '../../wizard-framework';
import { Step1_TemplateSettings } from './template-steps/Step1_TemplateSettings';
import { Step2_ContentDesigner } from './template-steps/Step2_ContentDesigner';
import { Step3_PreviewAndTest } from './template-steps/Step3_PreviewAndTest';
import { Step4_TestPdfPreview } from './template-steps/Step4_TestPdfPreview';
import { Step5_ReviewAndCreate } from './template-steps/Step5_ReviewAndCreate';

/**
 * DocumentTemplateCreateWizard
 *
 * Root component for multi-step document template creation/editing flow
 *
 * Architecture:
 * - Uses useWizardNavigation for abstract navigation state
 * - Maintains domain-specific wizardData state separately
 * - Steps communicate validation via setCanAdvance callback
 * - Supports both create and edit modes
 *
 * 5-Step Process:
 * 1. Configure template settings (title, category, type, acceptance mode)
 * 2. Design content using ContentBuilder or load from template
 * 3. Preview template and fill test form data
 * 4. Generate and review test PDF (optional)
 * 5. Review all settings and create/update template
 */
const DocumentTemplateCreateWizard = ({
  club_id,
  templateId = null,
  initialData = null,
  isEditMode = false,
  onClose,
  onSuccess
}) => {
  const TOTAL_STEPS = 5;

  // Abstract wizard navigation state (separated from domain state)
  const {
    currentStep,
    goToNext,
    goToPrevious,
    goToStep,
    setCanAdvance,
    setIsLoading,
    buttonStates
  } = useWizardNavigation(TOTAL_STEPS, {
    initialStep: 1,
    onStepChange: (step) => {
      console.log('[Template Wizard] Navigated to step:', step);
    }
  });

  // Domain-specific wizard data state - persists across all steps
  const [wizardData, setWizardData] = useState(() => {
    console.log('[DocumentTemplateCreateWizard] Initializing with:', { isEditMode, initialData, club_id, templateId });
    
    // If in edit mode and we have initial data, use it to populate the wizard
    if (isEditMode && initialData) {
      console.log('[DocumentTemplateCreateWizard] Populating wizard with template data');
      return {
        // Step 1: Template Settings
        title: initialData.title || '',
        description: initialData.description || '',
        category: initialData.category || 'waiver',
        acceptance_mode: initialData.acceptance_mode || 'accept',
        active: initialData.active !== false,

        // Step 2: Content
        content: initialData.content || '',
        elements_json: initialData.elements_json || '',
        elements: [], // Will be parsed from elements_json in Step 2

        // Step 3-4: Test Data & PDF
        testFormData: {},
        testPdfUrl: null,

        // Metadata
        club_id: club_id,
        isPlatformTemplate: club_id === 'platform',
        templateId: templateId // Store template ID for updates
      };
    }

    // Default state for create mode
    console.log('[DocumentTemplateCreateWizard] Using default state for create mode');
    return {
      // Step 1: Template Settings
      title: '',
      description: '',
      category: 'waiver',
      acceptance_mode: 'accept',
      active: true,

      // Step 2: Content
      content: '',
      elements_json: '',
      elements: [],

      // Step 3-4: Test Data & PDF
      testFormData: {},
      testPdfUrl: null,

      // Metadata
      club_id: club_id,
      isPlatformTemplate: club_id === 'platform',
      templateId: null
    };
  });

  // Handle step data changes (domain state)
  const handleDataChange = useCallback((newData) => {
    setWizardData(newData);
  }, []);

  // Handle step validation (drives wizard navigation state)
  const handleValidationChange = useCallback((isValid) => {
    setCanAdvance(isValid);
  }, [setCanAdvance]);

  // Handle successful template creation/update
  const handleCreateSuccess = useCallback((response) => {
    const action = isEditMode ? 'updated' : 'created';
    console.log(`[Template Wizard] Template ${action} successfully:`, response);
    setIsLoading(false);
    if (onSuccess) {
      onSuccess(response);
    }
    onClose();
  }, [isEditMode, onSuccess, onClose, setIsLoading]);

  // Handle creation error
  const handleCreateError = useCallback((error) => {
    console.error('[Template Wizard] Template creation error:', error);
    setIsLoading(false);
  }, [setIsLoading]);

  // Handle test PDF generation - advances to step 4
  const handleGenerateTestPdf = useCallback((pdfUrl) => {
    console.log('[Template Wizard] Test PDF generated, advancing to step 4');
    goToStep(4);
  }, [goToStep]);

  // Handle back to test from PDF preview - go back to step 3
  const handleBackToTest = useCallback(() => {
    console.log('[Template Wizard] Going back to test form');
    goToStep(3);
  }, [goToStep]);

  // Track if finish button was clicked to trigger submission
  const [triggerSubmit, setTriggerSubmit] = useState(false);

  // Finish wizard - trigger submission in Step 5
  const handleFinish = useCallback(() => {
    if (currentStep === TOTAL_STEPS) {
      // Trigger submission in Step 5
      setTriggerSubmit(true);
      // Reset after a brief delay to allow re-triggering if needed
      setTimeout(() => setTriggerSubmit(false), 100);
    }
  }, [currentStep, TOTAL_STEPS]);

  // Render current step component
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1_TemplateSettings
            wizardData={wizardData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 2:
        return (
          <Step2_ContentDesigner
            wizardData={wizardData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 3:
        return (
          <Step3_PreviewAndTest
            wizardData={wizardData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
            onGenerateTestPdf={handleGenerateTestPdf}
          />
        );

      case 4:
        return (
          <Step4_TestPdfPreview
            wizardData={wizardData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
            onBackToTest={handleBackToTest}
          />
        );

      case 5:
        return (
          <Step5_ReviewAndCreate
            wizardData={wizardData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
            onSuccess={handleCreateSuccess}
            onError={handleCreateError}
            setIsLoading={setIsLoading}
            triggerSubmit={triggerSubmit}
            isEditMode={isEditMode}
          />
        );

      default:
        return null;
    }
  };

  return (
    <WizardShell
      title={`${isEditMode ? 'Edit' : 'Create'} ${wizardData.isPlatformTemplate ? 'Platform' : 'Club'} Template`}
      totalSteps={TOTAL_STEPS}
      currentStep={currentStep}
      onNext={goToNext}
      onPrevious={goToPrevious}
      onFinish={handleFinish}
      onClose={onClose}
      buttonStates={buttonStates}
      nextButtonLabel={currentStep === 1 ? 'Continue' : 'Next'}
      previousButtonLabel="Back"
      finishButtonLabel={isEditMode ? 'Update Template' : 'Create Template'}
      isLoading={buttonStates.isLoading}
    >
      {renderCurrentStep()}
    </WizardShell>
  );
};

export { DocumentTemplateCreateWizard };
